import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Users as UsersIcon, Plus, Edit, Trash2, Key, Shield, CheckCircle, XCircle, Search, User, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800 border-red-300',
  coordenador: 'bg-purple-100 text-purple-800 border-purple-300',
  lider_pg: 'bg-blue-100 text-blue-800 border-blue-300',
  facilitador: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  voluntario: 'bg-green-100 text-green-800 border-green-300',
  visualizador: 'bg-gray-100 text-gray-800 border-gray-300',
};

const ROLE_ICONS = {
  admin: '👑',
  coordenador: '🎯',
  lider_pg: '👥',
  facilitador: '🤝',
  voluntario: '🙋',
  visualizador: '👀',
};

const Users = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [smallGroups, setSmallGroups] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Dialogs
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form state
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'voluntario',
    volunteer_id: '',
    assigned_pg_ids: [],
    is_active: true
  });
  const [newPassword, setNewPassword] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Search filters for dialog
  const [volunteerSearch, setVolunteerSearch] = useState('');
  const [pgSearch, setPgSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, pgsRes, volunteersRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/users/roles`),
        axios.get(`${API}/small-groups`),
        axios.get(`${API}/volunteers`)
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data.roles || {});
      setSmallGroups(pgsRes.data);
      setVolunteers(volunteersRes.data.filter(v => v.status === 'ativo'));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      phone: '',
      role: 'voluntario',
      volunteer_id: '',
      assigned_pg_ids: [],
      is_active: true
    });
    setVolunteerSearch('');
    setPgSearch('');
    setUserDialogOpen(true);
  };

  const handleOpenEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      volunteer_id: user.volunteer_id || '',
      assigned_pg_ids: user.assigned_pg_ids || [],
      is_active: user.is_active !== false
    });
    setVolunteerSearch('');
    setPgSearch('');
    setUserDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          phone: formData.phone || null,
          role: formData.role,
          volunteer_id: formData.volunteer_id || null,
          assigned_pg_ids: formData.assigned_pg_ids,
          is_active: formData.is_active
        };
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('Usuário atualizado!');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('Senha é obrigatória');
          return;
        }
        await axios.post(`${API}/users`, {
          ...formData,
          volunteer_id: formData.volunteer_id || null
        });
        toast.success('Usuário criado!');
      }
      
      setUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    try {
      await axios.post(`${API}/users/${editingUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Senha alterada!');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Erro ao resetar senha');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Usuário excluído!');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleTogglePG = (pgId) => {
    const current = formData.assigned_pg_ids || [];
    if (current.includes(pgId)) {
      setFormData({ ...formData, assigned_pg_ids: current.filter(id => id !== pgId) });
    } else {
      setFormData({ ...formData, assigned_pg_ids: [...current, pgId] });
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    if (searchTerm && !u.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !u.email.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (roleFilter !== 'all' && u.role !== roleFilter) {
      return false;
    }
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usuários e Permissões</h1>
          <p className="text-gray-500 mt-1">Gerencie os usuários do sistema e seus níveis de acesso</p>
        </div>
        <Button onClick={handleOpenCreateDialog} className="btn-primary">
          <Plus size={20} className="mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {Object.entries(roles).map(([key, role]) => {
          const count = users.filter(u => u.role === key).length;
          return (
            <div 
              key={key} 
              className={`card p-4 cursor-pointer transition hover:scale-105 ${roleFilter === key ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setRoleFilter(roleFilter === key ? 'all' : key)}
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl">{ROLE_ICONS[key]}</span>
                <span className="text-2xl font-bold">{count}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">{role.name}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por perfil" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os perfis</SelectItem>
              {Object.entries(roles).map(([key, role]) => (
                <SelectItem key={key} value={key}>
                  {ROLE_ICONS[key]} {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500">
            {filteredUsers.length} usuário(s)
          </span>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <table className="table w-full">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Email</th>
              <th>Perfil</th>
              <th>Voluntário Vinculado</th>
              <th>PGs Atribuídos</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className={!user.is_active ? 'opacity-50' : ''}>
                <td>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold">{user.name}</p>
                      {user.phone && <p className="text-xs text-gray-500">{user.phone}</p>}
                    </div>
                  </div>
                </td>
                <td className="text-gray-600">{user.email}</td>
                <td>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${ROLE_COLORS[user.role] || 'bg-gray-100'}`}>
                    {ROLE_ICONS[user.role]} {roles[user.role]?.name || user.role}
                  </span>
                </td>
                <td>
                  {user.linked_volunteer ? (
                    <div className="flex items-center gap-2">
                      {user.linked_volunteer.photo_url ? (
                        <img src={user.linked_volunteer.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-xs text-green-600">{user.linked_volunteer.name?.charAt(0)}</span>
                        </div>
                      )}
                      <span className="text-sm text-green-700">{user.linked_volunteer.name}</span>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td>
                  {user.assigned_pgs?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {user.assigned_pgs.slice(0, 2).map(pg => (
                        <span key={pg.id} className={`text-xs px-2 py-0.5 rounded ${pg.gender === 'masculino' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                          {pg.name}
                        </span>
                      ))}
                      {user.assigned_pgs.length > 2 && (
                        <span className="text-xs text-gray-500">+{user.assigned_pgs.length - 2}</span>
                      )}
                    </div>
                  ) : user.assigned_pg_ids?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {user.assigned_pg_ids.slice(0, 2).map(pgId => {
                        const pg = smallGroups.find(g => g.id === pgId);
                        return pg ? (
                          <span key={pgId} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            {pg.name}
                          </span>
                        ) : null;
                      })}
                      {user.assigned_pg_ids.length > 2 && (
                        <span className="text-xs text-gray-500">+{user.assigned_pg_ids.length - 2}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td>
                  {user.is_active !== false ? (
                    <span className="flex items-center text-green-600 text-sm">
                      <CheckCircle size={16} className="mr-1" /> Ativo
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600 text-sm">
                      <XCircle size={16} className="mr-1" /> Inativo
                    </span>
                  )}
                </td>
                <td>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleOpenEditDialog(user)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition"
                      title="Editar"
                    >
                      <Edit size={16} className="text-blue-600" />
                    </button>
                    <button
                      onClick={() => { setEditingUser(user); setResetPasswordDialogOpen(true); }}
                      className="p-2 hover:bg-yellow-50 rounded-lg transition"
                      title="Resetar senha"
                    >
                      <Key size={16} className="text-yellow-600" />
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => { setUserToDelete(user); setDeleteDialogOpen(true); }}
                        className="p-2 hover:bg-red-50 rounded-lg transition"
                        title="Excluir"
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <Tabs defaultValue="dados" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="dados" className="flex items-center gap-2">
                  <User size={16} />
                  Dados Básicos
                </TabsTrigger>
                <TabsTrigger value="vinculos" className="flex items-center gap-2">
                  <Link2 size={16} />
                  Vínculos
                  {(formData.assigned_pg_ids?.length > 0 || formData.volunteer_id) && (
                    <span className="ml-1 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {(formData.assigned_pg_ids?.length || 0) + (formData.volunteer_id ? 1 : 0)}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-y-auto pr-1">
                <TabsContent value="dados" className="mt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={!!editingUser}
                    />
                  </div>
                  
                  {!editingUser && (
                    <div>
                      <Label>Senha *</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label>Perfil *</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(roles).map(([key, role]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center">
                              <span className="mr-2">{ROLE_ICONS[key]}</span>
                              <div>
                                <p className="font-medium">{role.name}</p>
                                <p className="text-xs text-gray-500">{role.description}</p>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {editingUser && (
                    <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="rounded"
                      />
                      <Label htmlFor="is_active" className="cursor-pointer">Usuário ativo</Label>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="vinculos" className="mt-0 space-y-4">
                  {/* PGs Atribuídos */}
                  <div>
                    <Label>PGs Atribuídos</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Selecione os PGs associados a este usuário
                    </p>
                    <Input
                      placeholder="Buscar PG..."
                      value={pgSearch}
                      onChange={(e) => setPgSearch(e.target.value)}
                      className="mb-2"
                    />
                    <div className="max-h-36 overflow-y-auto border rounded-lg p-2 space-y-1">
                      {smallGroups
                        .filter(pg => pg.name.toLowerCase().includes(pgSearch.toLowerCase()))
                        .map(pg => (
                        <label key={pg.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.assigned_pg_ids?.includes(pg.id)}
                            onChange={() => handleTogglePG(pg.id)}
                            className="rounded"
                          />
                          <span className="text-sm">{pg.name}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${pg.gender === 'masculino' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                            {pg.gender === 'masculino' ? '♂' : '♀'}
                          </span>
                        </label>
                      ))}
                      {smallGroups.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">Nenhum PG cadastrado</p>
                      )}
                    </div>
                    {formData.assigned_pg_ids?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {formData.assigned_pg_ids.map(pgId => {
                          const pg = smallGroups.find(p => p.id === pgId);
                          return pg ? (
                            <span key={pgId} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                              {pg.name}
                              <button type="button" onClick={() => handleTogglePG(pgId)} className="hover:text-purple-900">×</button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Voluntário Vinculado */}
                  <div>
                    <Label>Voluntário Vinculado</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Vincule este usuário a um cadastro de voluntário existente
                    </p>
                    <Input
                      placeholder="Buscar voluntário..."
                      value={volunteerSearch}
                      onChange={(e) => setVolunteerSearch(e.target.value)}
                      className="mb-2"
                    />
                    <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                      <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="radio"
                          name="volunteer"
                          checked={!formData.volunteer_id}
                          onChange={() => setFormData({ ...formData, volunteer_id: '' })}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-500 italic">Nenhum voluntário vinculado</span>
                      </label>
                      {volunteers
                        .filter(v => v.name.toLowerCase().includes(volunteerSearch.toLowerCase()))
                        .slice(0, 15)
                        .map(vol => (
                        <label key={vol.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="radio"
                            name="volunteer"
                            checked={formData.volunteer_id === vol.id}
                            onChange={() => setFormData({ ...formData, volunteer_id: vol.id })}
                            className="rounded"
                          />
                          <div className="flex items-center gap-2">
                            {vol.photo_url ? (
                              <img src={vol.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-xs text-purple-600">{vol.name.charAt(0)}</span>
                              </div>
                            )}
                            <span className="text-sm">{vol.name}</span>
                          </div>
                        </label>
                      ))}
                      {volunteers.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">Nenhum voluntário ativo cadastrado</p>
                      )}
                    </div>
                    {formData.volunteer_id && (
                      <div className="mt-2">
                        {(() => {
                          const vol = volunteers.find(v => v.id === formData.volunteer_id);
                          return vol ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-2 w-fit">
                              {vol.photo_url ? (
                                <img src={vol.photo_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                              ) : null}
                              {vol.name}
                              <button type="button" onClick={() => setFormData({ ...formData, volunteer_id: '' })} className="hover:text-green-900">×</button>
                            </span>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </div>
            </Tabs>
            
            <div className="flex justify-end space-x-2 pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary">
                {editingUser ? 'Salvar' : 'Criar Usuário'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar Senha</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Definir nova senha para <strong>{editingUser?.name}</strong>
            </p>
            <div>
              <Label>Nova Senha</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleResetPassword} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                Resetar Senha
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja excluir o usuário <strong>{userToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-red-600">Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleDelete} variant="destructive">
                Excluir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
